import { TOD_OFFSET_MINUTES } from "@next-platform/packages-ts-types";
/**
 * Index of the level whose inclusive range contains `value` (levels are ordered ascending,
 * `to: null` = open upper bound). Values above the last level's bound stay in the last level, matching the
 * engine's `levelIndex`. `-1` only when there are no levels.
 */
export function resolveMeterLevelIndex({ levels, value }) {
    const index = levels.findIndex((level) => value <= (level.to ?? Infinity));
    return index === -1 ? levels.length - 1 : index;
}
/** A level's label may be shown when it is self-revealed or the meter has reached it before. */
export function isMeterLevelRevealed({ level, levelIndex, revealedIndex, }) {
    return level.revealed || levelIndex <= (revealedIndex ?? -1);
}
const MINUTES_PER_DAY = 1440;
/** Index of the clock `tod_levels` band containing `clock.minute_of_day`; band bounds are minutes since 06:00 (`TOD_OFFSET_MINUTES`). `-1` when absent. */
export function resolveClockTimeOfDayIndex({ meter, clock }) {
    if (!clock || !meter.tod_levels) {
        return -1;
    }
    const minutesSinceOffset = (clock.minute_of_day - TOD_OFFSET_MINUTES + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    return meter.tod_levels.findIndex((level) => minutesSinceOffset >= level.from && (level.to === null || minutesSinceOffset <= level.to));
}
/** Label of the clock `tod_levels` band containing `clock.minute_of_day`; band bounds are minutes since 06:00 (`TOD_OFFSET_MINUTES`). */
export function resolveClockTimeOfDayLabel({ meter, clock, }) {
    const index = resolveClockTimeOfDayIndex({ meter, clock });
    return index === -1 ? undefined : meter.tod_levels?.[index]?.label;
}
const MINUTES_PER_HOUR = 60;
const padMinute = (value) => String(value).padStart(2, "0");
/** `HH:MM` wall-clock label for a `minute_of_day` value. */
export function formatClockMinuteLabel({ minuteOfDay }) {
    const wrapped = ((minuteOfDay % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    return `${padMinute(Math.floor(wrapped / MINUTES_PER_HOUR))}:${padMinute(wrapped % MINUTES_PER_HOUR)}`;
}
/**
 * Range-column text for a level row: value or story-day bounds (`0–24` /
 * `61+` — day wording is added by the caller for the `days` kind), or a
 * wall-clock label for a time-of-day band (`HH:MM` of
 * `from + TOD_OFFSET_MINUTES`, mod 1440 — band bounds are stored as
 * minutes since 06:00, so the offset converts them back to wall time).
 */
export function formatMeterLevelRange({ level, kind }) {
    if (kind === "tod") {
        return formatClockMinuteLabel({ minuteOfDay: level.from + TOD_OFFSET_MINUTES });
    }
    return level.to === null ? `${level.from}+` : `${level.from}–${level.to}`;
}
const CHAR_TOKEN_PATTERN = /\{\{\s*char\s*\}\}/gi;
const USER_TOKEN_PATTERN = /\{\{\s*user\s*\}\}/gi;
/** Renders rulebook-facing text for the user: `{{char}}` → the character name, `{{user}}` → "you". */
export function renderRulebookUserText({ text, characterName }) {
    return text.replace(CHAR_TOKEN_PATTERN, (characterName ?? "").trim()).replace(USER_TOKEN_PATTERN, "you");
}
